import type { ChatMessage, ChatPerson } from "../../api/chat";
import { intlLocale } from "../../lib/format";

/**
 * A run of a message's text, already split into what the screen draws differently. Bold, italic, struck and a link's
 * label hold runs of their own, so *_both_* is bold and italic at once; code holds plain text.
 */
export type Inline =
  | { kind: "text"; text: string }
  | { kind: "code"; text: string }
  | { kind: "bold" | "italic" | "strike"; children: Inline[] }
  | { kind: "mention"; id: string }
  | { kind: "link"; href: string; children: Inline[] };

/**
 * A message is blocks: paragraphs (a "\n" inside one is a line break), fenced code, quotes ("> ") and lists ("- " or
 * "1. ").
 */
export type Block =
  | { kind: "text"; inline: Inline[] }
  | { kind: "pre"; text: string }
  | { kind: "quote"; inline: Inline[] }
  | { kind: "list"; ordered: boolean; start: number; items: Inline[][] };

const FENCE = /```\n?([\s\S]*?)```/g;
const INLINE = new RegExp(
  [
    "`([^`\\n]+)`", // code
    "\\*\\*([^\\n]+?)\\*\\*", // **bold**
    "\\*([^*\\n]+?)\\*", // *bold*
    "_([^_\\n]+?)_", // _italic_
    "~([^~\\n]+?)~", // ~struck~
    "<@([0-9a-fA-F-]{36})>", // a colleague
    "\\[([^\\]\\n]+)\\]\\((https?:\\/\\/[^\\s)]+)\\)", // [label](https://…)
    "(https?:\\/\\/[^\\s<>]+)", // a bare link
  ].join("|"),
  "g",
);
const WORD = /[\p{L}\p{N}]/u;
const LINK_TAIL = /[.,;:!?)\]'"]+$/;
const QUOTE = /^>\s?/;
const BULLET = /^\s*[-•*]\s+/;
const NUMBERED = /^\s*(\d+)[.)]\s+/;

/**
 * Reads a message's text the way the chat writes it: `code`, ```code block```, *bold* or **bold**, _italic_,
 * ~struck~, [label](link), <@id> for a colleague, bare http(s) links, "> " quotes and "- " or "1. " lists. Markers
 * inside a word (snake_case) stay text. Nothing here builds HTML: the screen turns each run into an element, so a
 * message can never inject markup.
 */
export function parseBody(body: string): Block[] {
  const blocks: Block[] = [];
  let at = 0;
  for (const m of body.matchAll(FENCE)) {
    const start = m.index ?? 0;
    if (start > at) blocks.push(...parseLines(trimNewlines(body.slice(at, start))));
    blocks.push({ kind: "pre", text: m[1].replace(/\n$/, "") });
    at = start + m[0].length;
  }
  if (at < body.length) blocks.push(...parseLines(trimNewlines(body.slice(at))));
  return blocks.filter((b) => b.kind !== "text" || b.inline.length > 0);
}

type LineKind = "text" | "quote" | "bullet" | "numbered";

/** Groups lines into paragraphs, quotes and lists, each a run of lines of one kind. */
function parseLines(text: string): Block[] {
  if (!text) return [];
  const out: Block[] = [];
  let run: { kind: LineKind; lines: string[]; start: number } | null = null;
  const flush = () => {
    if (!run) return;
    if (run.kind === "text") out.push({ kind: "text", inline: parseInline(run.lines.join("\n")) });
    else if (run.kind === "quote") out.push({ kind: "quote", inline: parseInline(run.lines.join("\n")) });
    else out.push({ kind: "list", ordered: run.kind === "numbered", start: run.start, items: run.lines.map(parseInline) });
    run = null;
  };
  for (const line of text.split("\n")) {
    // A numbered list starts at 1 and goes on with any number: „15. oktobra stiže kamion" is a date, not a list.
    const n = line.match(NUMBERED);
    const inList: boolean = run !== null && (run as { kind: LineKind }).kind === "numbered";
    const numbered: RegExpMatchArray | null = n && (Number(n[1]) === 1 || inList) ? n : null;
    const kind: LineKind = QUOTE.test(line) ? "quote" : BULLET.test(line) ? "bullet" : numbered ? "numbered" : "text";
    const content = kind === "quote" ? line.replace(QUOTE, "")
      : kind === "bullet" ? line.replace(BULLET, "")
      : kind === "numbered" ? line.replace(NUMBERED, "")
      : line;
    if (!run || run.kind !== kind) {
      flush();
      run = { kind, lines: [], start: numbered ? Number(numbered[1]) : 1 };
    }
    run.lines.push(content);
  }
  flush();
  return out;
}

function trimNewlines(s: string): string {
  return s.replace(/^\n+|\n+$/g, "");
}

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let at = 0;
  const pushText = (s: string) => {
    if (!s) return;
    const last = out[out.length - 1];
    if (last?.kind === "text") last.text += s;
    else out.push({ kind: "text", text: s });
  };
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    const [, code, bold2, bold1, italic, strike, mention, label, href, link] = m;
    const marker = bold1 !== undefined || italic !== undefined || strike !== undefined;
    // *bold*, _italic_ and ~struck~ only between word boundaries, so snake_case and 2*3*4 stay as written.
    if (marker && (WORD.test(text[start - 1] ?? "") || WORD.test(text[end] ?? ""))) continue;
    pushText(text.slice(at, start));
    if (code !== undefined) out.push({ kind: "code", text: code });
    else if (bold2 !== undefined) out.push({ kind: "bold", children: parseInline(bold2) });
    else if (bold1 !== undefined) out.push({ kind: "bold", children: parseInline(bold1) });
    else if (italic !== undefined) out.push({ kind: "italic", children: parseInline(italic) });
    else if (strike !== undefined) out.push({ kind: "strike", children: parseInline(strike) });
    else if (mention !== undefined) out.push({ kind: "mention", id: mention.toLowerCase() });
    else if (href !== undefined) out.push({ kind: "link", href, children: parseInline(label) });
    else if (link !== undefined) {
      const tail = link.match(LINK_TAIL)?.[0] ?? "";
      const bare = link.slice(0, link.length - tail.length);
      out.push({ kind: "link", href: bare, children: [{ kind: "text", text: bare.replace(/^https?:\/\//, "") }] });
      pushText(tail);
    }
    at = end;
  }
  pushText(text.slice(at));
  return out;
}

/**
 * A message as one line of plain text, for a preview: a colleague as @name, a link by its label, code as its text,
 * without the marks of bold, italics and strikes; paragraphs, lines and list items run on with spaces.
 */
export function plainLine(body: string, people: Map<string, ChatPerson>, unknown: string): string {
  const line = (runs: Inline[]): string => runs.map((r) => {
    switch (r.kind) {
      case "text":
      case "code":
        return r.text;
      case "mention":
        return `@${people.get(r.id)?.name ?? unknown}`;
      default:
        return line(r.children);
    }
  }).join("");
  return parseBody(body)
    .map((b) => (b.kind === "pre" ? b.text : b.kind === "list" ? b.items.map(line).join(" ") : line(b.inline)))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/** A message of one to a few emoji and nothing else, drawn large as Slack does. */
export function isEmojiOnly(body: string): boolean {
  const t = body.trim();
  if (!t || t.length > 24) return false;
  return /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|‍|️|\s)+$/u.test(t);
}

/** What the message list draws, in order: day dividers, the "new" line, and messages. */
export type Row =
  | { kind: "day"; key: string; label: string }
  | { kind: "new"; key: string }
  | { kind: "message"; key: string; message: ChatMessage; head: boolean };

/** Two messages by one author within this long read as one block under one name. */
const GROUP_MS = 5 * 60 * 1000;

/**
 * Lays out messages (oldest first) as Slack does: a divider for each day, a "new messages" line above the first
 * message from someone else after `newAfter`, and the author's name only where a block starts.
 */
export function layoutRows(
  messages: ChatMessage[],
  opts: { me: string; newAfter: string | null; now: Date; today: string; yesterday: string },
): Row[] {
  const rows: Row[] = [];
  let prev: ChatMessage | null = null;
  let newShown = false;
  const newAfter = opts.newAfter ? Date.parse(opts.newAfter) : null;
  for (const m of messages) {
    const at = Date.parse(m.createdAt);
    const day = dayKey(m.createdAt);
    let head = !prev || prev.authorId !== m.authorId || at - Date.parse(prev.createdAt) > GROUP_MS;
    if (!prev || dayKey(prev.createdAt) !== day) {
      rows.push({ kind: "day", key: `day-${day}`, label: dayLabel(m.createdAt, opts.now, opts.today, opts.yesterday) });
      head = true;
    }
    if (!newShown && newAfter !== null && at > newAfter && m.authorId !== opts.me) {
      rows.push({ kind: "new", key: "new" });
      newShown = true;
      head = true;
    }
    rows.push({ kind: "message", key: m.id, message: m, head });
    prev = m;
  }
  return rows;
}

/** The local calendar day of an instant, as yyyy-mm-dd. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function dayLabel(iso: string, now: Date, today: string, yesterday: string): string {
  const key = dayKey(iso);
  if (key === dayKey(now.toISOString())) return today;
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (key === dayKey(y.toISOString())) return yesterday;
  const d = new Date(iso);
  return new Intl.DateTimeFormat(intlLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  }).format(d);
}

export function timeOf(iso: string): string {
  return new Intl.DateTimeFormat(intlLocale(), { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

/** When a thread was last answered: the time if today, else a short date. */
export function shortWhen(iso: string, now: Date): string {
  if (dayKey(iso) === dayKey(now.toISOString())) return timeOf(iso);
  return new Intl.DateTimeFormat(intlLocale(), { day: "numeric", month: "short" }).format(new Date(iso));
}

export function dateTimeOf(iso: string): string {
  return new Intl.DateTimeFormat(intlLocale(), { dateStyle: "long", timeStyle: "short" }).format(new Date(iso));
}

/** When a found message was written: the time if today, else a short date with the time. */
export function foundWhen(iso: string, now: Date): string {
  if (dayKey(iso) === dayKey(now.toISOString())) return timeOf(iso);
  const d = new Date(iso);
  return new Intl.DateTimeFormat(intlLocale(), {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** A colleague picked from the @ list while writing: how the text shows them, and who they are. */
export type Picked = { id: string; label: string };

/** Turns each picked "@Name" in the text into the <@id> the server stores; the longest names go first. */
export function encodeMentions(text: string, picked: Picked[]): string {
  let out = text;
  for (const p of [...picked].sort((a, b) => b.label.length - a.label.length)) {
    out = out.split(p.label).join(`<@${p.id}>`);
  }
  return out;
}

/** The other way, for editing a message: <@id> back to "@Name", and the list to turn it again on save. */
export function decodeMentions(body: string, people: Map<string, ChatPerson>): { text: string; picked: Picked[] } {
  const picked: Picked[] = [];
  const text = body.replace(/<@([0-9a-fA-F-]{36})>/g, (whole, id: string) => {
    const person = people.get(id.toLowerCase());
    if (!person) return whole;
    const label = `@${person.name}`;
    if (!picked.some((p) => p.id === person.id)) picked.push({ id: person.id, label });
    return label;
  });
  return { text, picked };
}

/** The "@query" being written right before the caret, or null when the caret is not in one. */
export function mentionQuery(textBeforeCaret: string): string | null {
  const m = textBeforeCaret.match(/(?:^|\s)@([\p{L}\p{N}_.-]{0,30})$/u);
  return m ? m[1] : null;
}

/** People whose name matches the query, by first name, by any later name, then anywhere. */
export function matchPeople(people: ChatPerson[], query: string, limit = 8): ChatPerson[] {
  const q = fold(query);
  if (!q) return people.slice(0, limit);
  const score = (p: ChatPerson) => {
    const name = fold(p.name);
    if (name.startsWith(q)) return 0;
    if (name.split(/\s+/).some((w) => w.startsWith(q))) return 1;
    return name.includes(q) ? 2 : 3;
  };
  return people
    .map((p) => ({ p, s: score(p) }))
    .filter((x) => x.s < 3)
    .sort((a, b) => a.s - b.s || a.p.name.localeCompare(b.p.name))
    .slice(0, limit)
    .map((x) => x.p);
}

/** Lower case without accents, so "dza" finds "Džaković" and "sasa" finds "Saša". */
export function fold(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/đ/g, "dj");
}

/** One character as a message search compares it: lower case, without its accent, đ as d. */
function searchFold(ch: string): string {
  return ch.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/đ/g, "d");
}

/**
 * The words a message search looks for, taken as the server takes them: punctuation off either end, folded, and "dj"
 * as d (so "Djordje" finds "Đorđe"); two letters at least, the first eight.
 */
export function searchWords(q: string): string[] {
  const words = q
    .slice(0, 200)
    .trim()
    .split(/\s+/)
    .map((w) => [...w.replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, "")].map(searchFold).join("").replace(/dj/g, "d"))
    .filter((w) => w.length >= 2);
  return [...new Set(words)].slice(0, 8);
}

/**
 * Where the searched words stand in a text, as [start, end) in the text as written, in order and not overlapping. The
 * text is folded a character at a time like the words, keeping where each folded character came from.
 */
export function findWords(text: string, words: string[]): [number, number][] {
  if (words.length === 0 || !text) return [];
  let folded = "";
  const from: number[] = [];
  const to: number[] = [];
  // A d takes one j after it ("dj" is đ typed without the stroke), as the server's single pass does.
  let joined = false;
  let at = 0;
  for (const ch of text) {
    const next = at + ch.length;
    const f = searchFold(ch);
    for (let k = 0; k < f.length; k++) {
      if (f[k] === "j" && !joined && folded.endsWith("d")) {
        to[to.length - 1] = next;
        joined = true;
        continue;
      }
      folded += f[k];
      from.push(at);
      to.push(next);
      joined = false;
    }
    at = next;
  }
  const found: [number, number][] = [];
  for (const w of words) {
    for (let i = folded.indexOf(w); i >= 0; i = folded.indexOf(w, i + w.length)) {
      found.push([from[i], to[i + w.length - 1]]);
    }
  }
  found.sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  for (const [s, e] of found) {
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

/** A channel name as the server will store it; shown under the field while it is typed. */
export function channelSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^#+/, "")
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}_-]/gu, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}

/** One of six colour pairs, the same for a person on every screen. */
export function toneOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h) % 6;
}
